/**
 * public/js/chapter-splitter/wave-view.js — the waveform of /chapter-splitter: which part of the
 * book is on screen, what a gesture over it means, and the picture.
 *
 * WHY (bug report 21.10.2026, «самопроизвольно отключается и все что выделили исчезло надо
 * начинать по новому»; RU telemetry 21.08–21.10). The editor zoomed by stretching a canvas inside a
 * native scroll box and read the wheel one zoom STEP per event. On a Mac trackpad a pinch is a burst
 * of ~20 ctrl-wheel events, so one pinch jumped 1× → 50× and «Приблизить» then did nothing: all 39
 * sessions with a dead «+» were macOS, none of 41 Windows sessions. A horizontal two-finger swipe was
 * swallowed when zoomed (the box scrolled by deltaY, which a sideways swipe does not have) and fell
 * through to the browser at 1× — and on a Mac a sideways swipe the page does not take is «Back»: the
 * page left, every marker with it. The complainant restarted the same book 23 times in 6 days.
 * Past ~9× a 8192-px canvas cap also turned «zoom» into a blurry stretch of the same pixels.
 *
 * WHAT IT DOES NOW
 *   - The canvas is the size of what is visible; the picture is drawn for the visible window from
 *     the peaks (no cap, sharp at any zoom).
 *   - Ctrl/⌘ + wheel and a trackpad pinch zoom SMOOTHLY around the pointer: the moment under the
 *     pointer stays under it (factor = exp(−Δy·k), Δy normalised to pixels).
 *   - A sideways wheel or swipe pans the view and is always taken (never reaches the browser's
 *     history gesture); Shift + wheel pans for a mouse. A vertical wheel scrolls the page, as
 *     everywhere else on the site.
 *   - A finger or a mouse dragged sideways pans; two fingers pinch; a tap/click stays a click (the
 *     click that ends a drag is swallowed — only that press's).
 *   - Under the waveform, while zoomed: the whole book in miniature with the visible window on it
 *     (drag the window, or press anywhere to jump).
 *   - The zoom buttons are disabled at the limits — a press that cannot do anything is not offered.
 *
 *   CsWaveView.Viewport(duration, secondsPerPeak)        — the pure model (node-tested)
 *   CsWaveView.wheelIntent(e, pageHeight)                — {kind:'zoom',factor} | {kind:'pan',px} | null
 *   CsWaveView.attach({wrap, canvas, spec, overview, ovCanvas, ovWin, onChange, onClick, onDblClick})
 *       → { vp, setSource(src), setScene(scene), setSpectrogram(on, source), redraw(), flush(), zoomIn(t?),
 *           zoomOut(t?), reveal(t), tAt(clientX), xPct(t), lastPointerType(), interacting(), destroy() }
 *   Tests: tests/js/chapter-splitter-wave-view.test.mjs; browser: tests/playwright/chapter-splitter-trackpad.mjs
 */
(function (root) {
    'use strict';

    var MIN_SPAN_FLOOR = 1;      // s — the narrowest window, whatever the data allows
    var PX_PER_PEAK_AT_MAX = 2;  // at full zoom one peak bucket is at most this wide
    /* exp(−Δy/100): Chromium turns each step of a trackpad pinch into a ctrl-wheel event with
       Δy = −100·ln(scale) (touchpad_pinch_event_queue.cc, CL 250923004), so this factor IS the scale
       the fingers made — a pinch zooms as far as the fingers moved, however many events it is cut
       into. A mouse notch with Ctrl (Δy = 100) is then ×2.7, held to ×2 by the clamp below. */
    var ZOOM_PER_PX = 0.01;
    var SLOP = { mouse: 4, pen: 6, touch: 8 };

    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

    /* ── the visible window ── */
    function Viewport(duration, secondsPerPeak) {
        this.duration = duration > 0 ? duration : 0;
        this.resolution = secondsPerPeak > 0 ? secondsPerPeak : 0;
        this.width = 1;
        this.start = 0;
        this.span = this.duration;
    }
    Viewport.prototype.minSpan = function () {
        var byData = this.resolution * Math.max(1, this.width) / PX_PER_PEAK_AT_MAX;
        return Math.min(this.duration, Math.max(MIN_SPAN_FLOOR, byData));
    };
    Viewport.prototype.clamp = function () {
        if (!(this.duration > 0)) { this.start = 0; this.span = 0; return this; }
        this.span = clamp(this.span, this.minSpan(), this.duration);
        this.start = clamp(this.start, 0, this.duration - this.span);
        return this;
    };
    Viewport.prototype.setWidth = function (px) { this.width = Math.max(1, px || 1); return this.clamp(); };
    Viewport.prototype.fit = function () { this.start = 0; this.span = this.duration; return this; };
    Viewport.prototype.zoom = function () { return this.span > 0 ? this.duration / this.span : 1; };
    Viewport.prototype.canZoomIn = function () { return this.span > this.minSpan() * 1.001; };
    Viewport.prototype.canZoomOut = function () { return this.span < this.duration * 0.999; };
    Viewport.prototype.zoomed = function () { return this.canZoomOut(); };
    /* factor > 1 zooms in; the moment t keeps its place on screen */
    Viewport.prototype.zoomAt = function (factor, t) {
        if (!(factor > 0) || !(this.span > 0)) return this;
        var rel = clamp((t - this.start) / this.span, 0, 1);
        var span = clamp(this.span / factor, this.minSpan(), this.duration);
        var at = clamp(t, 0, this.duration);
        this.span = span;
        this.start = at - rel * span;
        return this.clamp();
    };
    Viewport.prototype.panBy = function (dt) { this.start += dt; return this.clamp(); };
    Viewport.prototype.panPx = function (px) { return this.panBy(px * this.span / this.width); };
    Viewport.prototype.tToX = function (t) { return (t - this.start) / this.span * this.width; };
    Viewport.prototype.xToT = function (x) { return clamp(this.start + x / this.width * this.span, 0, this.duration); };
    Viewport.prototype.visible = function (t) { return t >= this.start && t <= this.start + this.span; };
    /* a moment that left the window comes back near its left edge (playback reads forward) */
    Viewport.prototype.reveal = function (t, lead) {
        if (this.visible(t)) return false;
        this.start = t - this.span * (lead == null ? 0.05 : lead);
        this.clamp();
        return true;
    };
    Viewport.prototype.centerOn = function (t) { this.start = t - this.span / 2; return this.clamp(); };
    Viewport.prototype.label = function () {
        var z = this.zoom();
        if (z < 1.05) return '1x';
        return (z < 10 ? (Math.round(z * 10) / 10) : Math.round(z)) + 'x';
    };

    /* ── what a wheel event over the waveform asks for ── */
    function wheelIntent(e, pageHeight) {
        if (!e) return null;
        var k = e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? (pageHeight || 800) : 1);
        var dx = (e.deltaX || 0) * k, dy = (e.deltaY || 0) * k;
        if (e.ctrlKey || e.metaKey) {
            if (!dy) return null;
            return { kind: 'zoom', factor: clamp(Math.exp(-dy * ZOOM_PER_PX), 0.5, 2) };
        }
        if (Math.abs(dx) > Math.abs(dy)) return { kind: 'pan', px: dx };
        if (e.shiftKey && dy) return { kind: 'pan', px: dy };
        return null;   // vertical: the page scrolls
    }

    /* ── pictures ── */
    function fft(re, im, n) {
        var bits = Math.round(Math.log2(n)), i, j;
        for (i = 0; i < n; i++) {
            var x = i, r = 0;
            for (j = 0; j < bits; j++) { r = (r << 1) | (x & 1); x >>= 1; }
            if (r > i) { var tr = re[i]; re[i] = re[r]; re[r] = tr; var ti = im[i]; im[i] = im[r]; im[r] = ti; }
        }
        for (var size = 2; size <= n; size *= 2) {
            var half = size / 2, ang = -2 * Math.PI / size;
            for (i = 0; i < n; i += size) {
                for (j = 0; j < half; j++) {
                    var wr = Math.cos(ang * j), wi = Math.sin(ang * j), a = i + j, b = a + half;
                    var xr = re[b] * wr - im[b] * wi, xi = re[b] * wi + im[b] * wr;
                    re[b] = re[a] - xr; im[b] = im[a] - xi; re[a] += xr; im[a] += xi;
                }
            }
        }
    }
    function specColour(val) {
        if (val < 0.25) return [0, Math.floor(val * 4 * 255), Math.floor(128 + val * 4 * 127)];
        if (val < 0.5) return [0, 255, Math.floor(255 - (val - 0.25) * 4 * 255)];
        if (val < 0.75) return [Math.floor((val - 0.5) * 4 * 255), 255, 0];
        return [255, Math.floor(255 - (val - 0.75) * 4 * 255), 0];
    }
    function texLimit() { try { return root.__canvasTexLimit ? root.__canvasTexLimit() : 8192; } catch (_) { return 8192; } }
    function sizeCanvas(c, cssW, cssH) {
        var dpr = root.devicePixelRatio || 1;
        dpr = Math.min(dpr, texLimit() / Math.max(cssW, 1));
        var w = Math.max(1, Math.round(cssW * dpr)), h = Math.max(1, Math.round(cssH * dpr));
        if (c.width !== w) c.width = w;
        if (c.height !== h) c.height = h;
        return { w: w, h: h, dpr: dpr };
    }
    /* min/max of the peaks between two moments */
    function peakRange(src, t0, t1) {
        var b0 = Math.floor(t0 / src.bucketSec), b1 = Math.max(b0 + 1, Math.ceil(t1 / src.bucketSec));
        if (b0 < 0) b0 = 0;
        if (b1 > src.len) b1 = src.len;
        var lo = 1, hi = -1;
        for (var b = b0; b < b1; b++) {
            var mn = src.mins[b], mx = src.maxs[b];
            if (mn < lo) lo = mn;
            if (mx > hi) hi = mx;
        }
        return b0 < b1 ? [lo, hi] : null;
    }
    function drawWave(canvas, vp, src, scene) {
        var cssW = canvas.clientWidth || vp.width, cssH = canvas.clientHeight || 160;
        var s = sizeCanvas(canvas, cssW, cssH), n = s.w, H = s.h;
        var ctx = canvas.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, n, H);
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        ctx.fillRect(0, Math.floor(H / 2), n, Math.max(1, Math.round(s.dpr)));
        if (!src || !(vp.span > 0)) return;
        var chapters = (scene && scene.chapters) || [], ci = 0, colour = '';
        ctx.globalAlpha = 0.85;
        for (var i = 0; i < n; i++) {
            var t0 = vp.start + i / n * vp.span, t1 = vp.start + (i + 1) / n * vp.span;
            var r = peakRange(src, t0, t1);
            if (!r) continue;
            var tm = (t0 + t1) / 2;
            while (ci < chapters.length - 1 && tm >= chapters[ci].end) ci++;
            var c = chapters.length ? chapters[ci].color : '#22c55e';
            if (c !== colour) { ctx.fillStyle = c; colour = c; }
            var y1 = (1 - r[1]) / 2 * H, y2 = (1 - r[0]) / 2 * H;
            ctx.fillRect(i, y1, 1, Math.max(1, y2 - y1));
        }
        ctx.globalAlpha = 1;
        var marks = (scene && scene.markers) || [];
        ctx.strokeStyle = '#f59e0b';
        ctx.lineWidth = Math.max(1, s.dpr);
        ctx.setLineDash([4 * s.dpr, 4 * s.dpr]);
        for (var m = 0; m < marks.length; m++) {
            if (!vp.visible(marks[m])) continue;
            var x = Math.round((marks[m] - vp.start) / vp.span * n) + 0.5;
            ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
        }
        ctx.setLineDash([]);
    }
    /* the spectrogram of the visible window: a column per CSS pixel. A long book's columns were
       taken during the envelope pass (spec.every frames apart) — a pixel takes the nearest one. */
    function drawSpec(canvas, vp, spec) {
        var cssW = canvas.clientWidth || vp.width, cssH = canvas.clientHeight || 160;
        var s = sizeCanvas(canvas, cssW, cssH);
        var ctx = canvas.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        var img = ctx.createImageData(s.w, s.h), px = img.data;
        var cols = Math.max(1, Math.round(cssW)), size = 1024, bins = size >> 1;
        var re = new Float32Array(size), im = new Float32Array(size), hann = new Float32Array(size), k;
        for (k = 0; k < size; k++) hann[k] = 0.5 * (1 - Math.cos(2 * Math.PI * k / (size - 1)));
        var env = spec && spec.env, data = spec && spec.data, sr = (spec && spec.sampleRate) || 44100;
        for (var col = 0; col < cols; col++) {
            var t = vp.start + (col + 0.5) / cols * vp.span, row0 = -1;
            if (env) {
                var E = env.spec;
                if (!E || !E.count) continue;
                row0 = clamp(Math.round(t * env.sampleRate / E.every), 0, E.count - 1);
            } else if (data) {
                var c0 = Math.floor(t * sr) - (size >> 1);
                for (k = 0; k < size; k++) { var idx = c0 + k; re[k] = (idx >= 0 && idx < data.length) ? data[idx] * hann[k] : 0; im[k] = 0; }
                fft(re, im, size);
            } else continue;
            var x0 = Math.round(col / cols * s.w), x1 = Math.max(x0 + 1, Math.round((col + 1) / cols * s.w));
            for (var y = 0; y < s.h; y++) {
                var fi = Math.min(bins - 1, Math.floor((1 - y / s.h) * bins)), val;
                if (env) val = env.spec.data[row0 * env.spec.bins + fi] / 255;
                else { var mag = Math.sqrt(re[fi] * re[fi] + im[fi] * im[fi]); val = clamp((20 * Math.log10(Math.max(mag, 1e-10)) + 60) / 60, 0, 1); }
                var rgb = specColour(val);
                for (var x = x0; x < x1; x++) { var p = (y * s.w + x) * 4; px[p] = rgb[0]; px[p + 1] = rgb[1]; px[p + 2] = rgb[2]; px[p + 3] = 200; }
            }
        }
        ctx.putImageData(img, 0, 0);
    }

    /* ── the DOM ── */
    function attach(o) {
        var wrap = o.wrap, canvas = o.canvas, specCanvas = o.spec || null;
        var ov = o.overview || null, ovCanvas = o.ovCanvas || null, ovWin = o.ovWin || null;
        var vp = new Viewport(0, 0), src = null, scene = { chapters: [], markers: [] }, spec = null, showSpec = false;
        var frame = 0, ovDirty = true, lastType = 'mouse', destroyed = false, swipes = 0;

        /* a pinch is dozens of events a second: the page hears about the view once per frame */
        var pendingKind = '';
        function notify(kind) { if (o.onChange) { try { o.onChange(vp, kind); } catch (e) { if (root.console) console.error(e); } } }
        function paint() {
            frame = 0;
            if (destroyed) return;
            var kind = pendingKind; pendingKind = '';
            drawWave(canvas, vp, src, scene);
            if (showSpec && specCanvas && spec) drawSpec(specCanvas, vp, spec);
            paintOverview();
            notify(kind || 'view');
        }
        function schedule(kind) {
            if (!pendingKind || kind === 'source') pendingKind = kind || 'view';
            if (!frame) frame = (root.requestAnimationFrame || setTimeout)(paint);
        }
        function paintOverview() {
            if (!ov) return;
            var on = !!src && vp.zoomed();
            if (ov.hidden === on) ov.hidden = !on;
            if (!on) return;
            if (ovDirty && ovCanvas) {
                ovDirty = false;
                var whole = new Viewport(vp.duration, vp.resolution).setWidth(ovCanvas.clientWidth || vp.width).fit();
                drawWave(ovCanvas, whole, src, scene);
            }
            if (ovWin) {
                ovWin.style.left = (vp.start / vp.duration * 100) + '%';
                ovWin.style.width = Math.max(0.6, vp.span / vp.duration * 100) + '%';
            }
        }
        function measure() { vp.setWidth(wrap.clientWidth || 1); }
        function localX(clientX) { var r = wrap.getBoundingClientRect(); return clamp(clientX - r.left, 0, r.width); }

        /* wheel: ctrl/⌘ or a pinch zooms, a sideways swipe (or Shift) pans, vertical scrolls the page */
        function onWheel(e) {
            if (!src) return;
            var w = wheelIntent(e, root.innerHeight);
            if (!w) return;
            e.preventDefault();
            if (w.kind === 'zoom') { vp.zoomAt(w.factor, vp.xToT(localX(e.clientX))); schedule('wheel'); return; }
            swipes++;
            vp.panPx(w.px);
            schedule('pan');
        }

        /* pointers: a sideways drag pans, two fingers pinch, the rest stays a click */
        var press = null, pts = {}, nPts = 0, pinch = null, swallow = 0;
        function onDown(e) {
            lastType = e.pointerType || 'mouse';
            swallow = 0;
            if (!src) return;
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            if (e.target && e.target.closest && e.target.closest('.cs-split-marker')) return;
            if (!pts[e.pointerId]) nPts++;
            pts[e.pointerId] = { x: e.clientX, y: e.clientY };
            if (nPts === 2 && e.pointerType === 'touch') { startPinch(); return; }
            if (nPts > 1) return;
            press = { id: e.pointerId, type: lastType, x0: e.clientX, y0: e.clientY, lastX: e.clientX, panning: false };
        }
        function startPinch() {
            var ids = Object.keys(pts), a = pts[ids[0]], b = pts[ids[1]];
            press = null;
            pinch = { d: Math.max(8, Math.hypot(a.x - b.x, a.y - b.y)), span: vp.span,
                t: vp.xToT(localX((a.x + b.x) / 2)), x: localX((a.x + b.x) / 2) };
        }
        function onMove(e) {
            if (pts[e.pointerId]) pts[e.pointerId] = { x: e.clientX, y: e.clientY };
            if (pinch) {
                var ids = Object.keys(pts);
                if (ids.length < 2) return;
                var a = pts[ids[0]], b = pts[ids[1]];
                var d = Math.max(8, Math.hypot(a.x - b.x, a.y - b.y)), cx = localX((a.x + b.x) / 2);
                vp.span = clamp(pinch.span * pinch.d / d, vp.minSpan(), vp.duration);
                vp.start = pinch.t - cx / vp.width * vp.span;
                vp.clamp();
                swallow = 1;
                schedule('pinch');
                return;
            }
            if (!press || e.pointerId !== press.id) return;
            if (!press.panning) {
                var dx = e.clientX - press.x0, dy = e.clientY - press.y0;
                if (Math.abs(dx) < (SLOP[press.type] || 4) || Math.abs(dx) < Math.abs(dy)) return;
                press.panning = true;
                press.lastX = e.clientX;
                try { wrap.setPointerCapture(e.pointerId); } catch (_) {}
                wrap.classList.add('cs-panning');
            }
            vp.panPx(press.lastX - e.clientX);
            press.lastX = e.clientX;
            schedule('drag');
        }
        function onUp(e) {
            if (pts[e.pointerId]) { delete pts[e.pointerId]; nPts--; }
            if (pinch && nPts < 2) { pinch = null; swallow = 1; }
            if (press && e.pointerId === press.id) {
                if (press.panning) swallow = e.type === 'pointercancel' ? 0 : 1;
                wrap.classList.remove('cs-panning');
                press = null;
            }
        }
        /* the click that ends a drag or a pinch is not a click — only that press's (detail ≠ 0) */
        function onClickCapture(e) {
            if (swallow && e.detail !== 0) { swallow = 0; e.stopImmediatePropagation(); e.preventDefault(); }
        }
        function onClick(e) {
            if (!src || !o.onClick) return;
            if (e.target && e.target.closest && e.target.closest('.cs-split-marker')) return;
            o.onClick(vp.xToT(localX(e.clientX)), e, lastType);
        }
        function onDbl(e) {
            if (!src || !o.onDblClick) return;
            if (e.target && e.target.closest && e.target.closest('.cs-split-marker')) return;
            o.onDblClick(vp.xToT(localX(e.clientX)), e);
        }

        /* the overview: the window is dragged, a press elsewhere jumps there and keeps dragging */
        var ovGrab = null;
        function ovT(clientX) { var r = ov.getBoundingClientRect(); return clamp((clientX - r.left) / Math.max(1, r.width), 0, 1) * vp.duration; }
        function onOvDown(e) {
            if (!src || (e.pointerType === 'mouse' && e.button !== 0)) return;
            e.preventDefault();
            var t = ovT(e.clientX);
            if (t < vp.start || t > vp.start + vp.span) { vp.centerOn(t); schedule('overview'); }
            ovGrab = { id: e.pointerId, off: t - vp.start };
            try { ov.setPointerCapture(e.pointerId); } catch (_) {}
        }
        function onOvMove(e) {
            if (!ovGrab || e.pointerId !== ovGrab.id) return;
            vp.start = ovT(e.clientX) - ovGrab.off;
            vp.clamp();
            schedule('overview');
        }
        function onOvUp(e) { if (ovGrab && e.pointerId === ovGrab.id) ovGrab = null; }

        wrap.addEventListener('wheel', onWheel, { passive: false });
        wrap.addEventListener('pointerdown', onDown);
        wrap.addEventListener('pointermove', onMove);
        wrap.addEventListener('pointerup', onUp);
        wrap.addEventListener('pointercancel', onUp);
        wrap.addEventListener('click', onClickCapture, true);
        wrap.addEventListener('click', onClick);
        wrap.addEventListener('dblclick', onDbl);
        if (ov) {
            ov.addEventListener('pointerdown', onOvDown);
            ov.addEventListener('pointermove', onOvMove);
            ov.addEventListener('pointerup', onOvUp);
            ov.addEventListener('pointercancel', onOvUp);
            ov.addEventListener('wheel', onWheel, { passive: false });
        }
        var ro = null;
        if (root.ResizeObserver) {
            ro = new root.ResizeObserver(function () { measure(); ovDirty = true; schedule('resize'); });
            ro.observe(wrap);
        } else root.addEventListener('resize', function () { measure(); ovDirty = true; schedule('resize'); });

        return {
            vp: vp,
            /* src: {duration, peaks:{mins,maxs,len}, bucketSec} or null */
            setSource: function (s) {
                src = s ? { mins: s.peaks.mins, maxs: s.peaks.maxs, len: s.peaks.len, bucketSec: s.bucketSec } : null;
                spec = null;
                vp.duration = s ? s.duration : 0;
                vp.resolution = s ? s.bucketSec : 0;
                measure();
                vp.fit().clamp();
                ovDirty = true;
                schedule('source');
            },
            setScene: function (sc) { scene = sc || { chapters: [], markers: [] }; ovDirty = true; schedule('scene'); },
            /* spec: {env} — a long book's columns — or {data, sampleRate} — the samples of channel 0 */
            setSpectrogram: function (on, source) { showSpec = !!on; if (source) spec = source; schedule('spec'); },
            /* a frame now, not at the next animation frame (a test, a page about to be measured) */
            flush: function () { if (frame) { (root.cancelAnimationFrame || clearTimeout)(frame); paint(); } },
            redraw: function () { measure(); ovDirty = true; schedule('redraw'); },
            zoomIn: function (t) { vp.zoomAt(2, t == null ? vp.start + vp.span / 2 : t); schedule('button'); },
            zoomOut: function (t) { vp.zoomAt(0.5, t == null ? vp.start + vp.span / 2 : t); schedule('button'); },
            reveal: function (t, lead) { if (vp.reveal(t, lead)) schedule('follow'); },
            tAt: function (clientX) { return vp.xToT(localX(clientX)); },
            /* where t sits, in % of the visible width (null when off screen) */
            xPct: function (t) { return vp.span > 0 && vp.visible(t) ? (t - vp.start) / vp.span * 100 : null; },
            lastPointerType: function () { return lastType; },
            /* sideways wheel/swipe events taken (each one a «Back» the browser did not get) */
            swipes: function () { return swipes; },
            interacting: function () { return !!(press && press.panning) || !!pinch || !!ovGrab; },
            destroy: function () {
                destroyed = true;
                if (ro) ro.disconnect();
                wrap.removeEventListener('wheel', onWheel);
            }
        };
    }

    root.CsWaveView = { Viewport: Viewport, wheelIntent: wheelIntent, attach: attach, _peakRange: peakRange, ZOOM_PER_PX: ZOOM_PER_PX };
})(typeof window !== 'undefined' ? window : globalThis);
